import {CallToActionNotice, EmptyMessage} from '#/components/lands/empty-lands';
import {LoadingLands} from '#/components/lands/loading-lands';
import {PortfolioAccountContext} from '#/components/portfolio/context/portfolio-account-context';
import {PortfolioNoContentContainer} from '#/components/portfolio/subcomponents/portfolio-no-content-container';
import {TransactionTileLarge} from '#/components/transaction/transaction-tile';
import {useRefSrollIntoView} from '#/hooks/use-ref-scroll-into-view';
import {useTransactionLandAndInfos} from '#/hooks/use-transaction-land-and-info';
import {useAccountTransactions} from '#/hooks/use-transactions';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

export const PortfolioActivity: FunctionComponent = () => {
  const {t} = useTranslation('translation');

  const {account, isCurrentUser} = useContext(PortfolioAccountContext);

  const {
    data: transactions,
    isApiLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useAccountTransactions(account, 10);
  const transactionLandAndInfos = useTransactionLandAndInfos(transactions);

  const [bottomElementRef, bottomElementScrollIntoView] = useRefSrollIntoView<HTMLDivElement>(100);

  const isLoading = isApiLoading && !isFetchingNextPage;
  const isEmpty = !isLoading && transactionLandAndInfos && transactionLandAndInfos.length === 0;

  const onShowNextClick = async () => {
    await fetchNextPage();
    bottomElementScrollIntoView();
  };

  if (isLoading) {
    return (
      <PortfolioNoContentContainer>
        <LoadingLands />
      </PortfolioNoContentContainer>
    );
  }

  if (isEmpty) {
    return (
      <PortfolioNoContentContainer>
        {isCurrentUser ? (
          <CallToActionNotice
            title={t('pages.portfolio.sections.activity.emptyContent.currentUser.ctaTitle')}
            subtitle={t('pages.portfolio.sections.activity.emptyContent.currentUser.ctaSubtitle')}
          />
        ) : (
          <EmptyMessage>{t('pages.portfolio.sections.activity.emptyContent.otherUser.message')}</EmptyMessage>
        )}
      </PortfolioNoContentContainer>
    );
  }

  return (
    <div className='grow flex flex-col gap-4 overflow-scroll relative'>
      {transactionLandAndInfos?.map(({land, transaction}, index) => (
        <TransactionTileLarge key={index} land={land} transaction={transaction} />
      ))}
      {transactions && hasNextPage && (
        <button
          className='grow h-14 py-4 rounded-xl text-white opacity-50 hover:opacity-100 border-2 font-semibold border-white bg-transparent flex-center'
          onClick={onShowNextClick}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? t('statuses.loadingDots') : t('actions.showMore')}
        </button>
      )}
      <div className='w-full h-0 absolute bottom-0' ref={bottomElementRef} />
    </div>
  );
};
